import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAuth } from "../../../context/AuthContext";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";
import { useAlert } from "../../../context/AlertContext";

const allRoles = [
  { label: "Administrador", value: "admin" },
  { label: "Staff", value: "staff" },
];

const initialForm = {
  nombre: "",
  correo: "",
  telefono: "",
  rol: "staff",
};

const NewUser = ({
  open,
  onClose,
  onCreated,
  loginUrl = "",
  createEndpoint = "/api/auth/users/create/",
}) => {
  const { accessToken, logout } = useAuth();
  const { dashboardTheme } = useDashboardTheme();
  const alert = useAlert();

  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const roles = useMemo(() => allRoles, []);

  const notify = (message, severity = "success") => {
    if (typeof alert?.showAlert === "function") {
      alert.showAlert(message, severity);
    }
  };

  const getToken = () => accessToken || localStorage.getItem("accessToken") || "";

  const parseJsonSafe = async (res) => {
    try {
      return await res.json();
    } catch {
      return { ok: false, error: "Respuesta inválida del servidor" };
    }
  };

  const authFetch = async (url, options = {}) => {
    const token = getToken();

    const headers = {
      Accept: "application/json",
      ...(options.headers || {}),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  useEffect(() => {
    if (open) {
      setForm(initialForm);
    }
  }, [open]);

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: dashboardTheme.card_background,
      color: dashboardTheme.text_color,
      "& fieldset": {
        borderColor: dashboardTheme.border_color,
      },
      "&:hover fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
      "&.Mui-focused fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
    },
    "& .MuiInputLabel-root": {
      color: dashboardTheme.muted_text_color,
      "&.Mui-focused": {
        color: dashboardTheme.primary_color,
      },
    },
    "& .MuiSvgIcon-root": {
      color: dashboardTheme.muted_text_color,
    },
  };

  const primaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    bgcolor: dashboardTheme.button_color,
    color: dashboardTheme.button_text_color,
    boxShadow: "none",
    "&:hover": {
      bgcolor: dashboardTheme.button_hover_color || dashboardTheme.button_color,
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    color: dashboardTheme.text_color,
    borderColor: dashboardTheme.border_color,
    "&:hover": {
      borderColor: dashboardTheme.primary_color,
      bgcolor: `${dashboardTheme.primary_color}12`,
    },
  };

  const handleChange = (event) => {
    setForm((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const resetForm = () => {
    setForm(initialForm);
  };

  const crearUsuario = async () => {
    if (!form.nombre.trim() || !form.correo.trim()) {
      notify("Nombre y correo son obligatorios", "error");
      return;
    }

    if (!loginUrl) {
      notify("Configura primero la URL de login para las invitaciones.", "error");
      return;
    }

    if (!["admin", "staff"].includes(form.rol)) {
      notify("Rol no permitido.", "error");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name: form.nombre.trim(),
        email: form.correo.trim().toLowerCase(),
        phone: form.telefono.trim(),
        role: form.rol,
        nombre: form.nombre.trim(),
        correo: form.correo.trim().toLowerCase(),
        telefono: form.telefono.trim(),
        rol: form.rol,
        login_url: loginUrl,
      };

      const res = await authFetch(createEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (res.ok && data.ok) {
        resetForm();
        notify(data?.msg || data?.message || "Usuario creado correctamente", "success");
        if (onCreated) await onCreated();
      } else {
        notify(data?.error || data?.msg || "Error al crear usuario", "error");
      }
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(460px, calc(100vw - 32px))",
          bgcolor: dashboardTheme.card_background,
          borderRadius: "16px",
          p: { xs: 2, sm: 3 },
          border: `1px solid ${dashboardTheme.border_color}`,
          boxShadow:
            dashboardTheme.theme_mode === "dark"
              ? "0 24px 60px rgba(0,0,0,0.45)"
              : "0 24px 60px rgba(17,24,39,0.18)",
          outline: "none",
        }}
      >
        <Typography
          sx={{
            mb: 2,
            fontWeight: 600,
            fontSize: 18,
            color: dashboardTheme.primary_color,
          }}
        >
          Crear nuevo usuario
        </Typography>

        <Stack spacing={2}>
          <TextField
            label="Nombre completo"
            name="nombre"
            fullWidth
            size="small"
            value={form.nombre}
            onChange={handleChange}
            disabled={saving}
            sx={fieldSx}
          />

          <TextField
            label="Correo electrónico"
            name="correo"
            fullWidth
            size="small"
            value={form.correo}
            onChange={handleChange}
            disabled={saving}
            sx={fieldSx}
          />

          <TextField
            label="Teléfono"
            name="telefono"
            fullWidth
            size="small"
            value={form.telefono}
            onChange={handleChange}
            disabled={saving}
            sx={fieldSx}
          />

          <TextField
            label="Rol"
            name="rol"
            select
            fullWidth
            size="small"
            value={form.rol}
            onChange={handleChange}
            disabled={saving}
            sx={fieldSx}
          >
            {roles.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="URL de login"
            fullWidth
            size="small"
            value={loginUrl}
            disabled
            sx={fieldSx}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={saving}
              sx={secondaryButtonSx}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={crearUsuario}
              disabled={saving}
              sx={primaryButtonSx}
            >
              {saving ? "Guardando..." : "Guardar usuario"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};

export default NewUser;